
import React from 'react';
import { BlockTimeDialog } from '@/components/booking/BlockTimeDialog';
import { EditBlockDialog } from '@/components/booking/EditBlockDialog';
import { BookingActionDialog } from '@/components/booking/BookingActionDialog';
import { TravelBlockCancelDialog } from '@/components/booking/TravelBlockCancelDialog';
import { UnifiedBookingDialog } from '@/components/booking/UnifiedBookingDialog';
import { BookingCompletionData } from '@/components/booking/BookingCompletionDialog';
import { parseStoredDateTime } from '@/utils/timeUtils';

interface DashboardDialogsProps {
  serviceName: string;
  selectedDate: Date;
  selectedTimeSlot: string;
  isBlockingDialogOpen: boolean;
  isModifyDialogOpen: boolean;
  isDeleteDialogOpen: boolean;
  isTravelCancelDialogOpen: boolean;
  isBookingDetailsOpen: boolean;
  selectedBlockedBooking: any | null;
  selectedTravelBlock: any | null;
  selectedBooking: any | null;
  loading?: boolean;
  serviceId: string;
  onCloseBlockingDialog: () => void;
  onCloseModifyDialog: () => void;
  onCloseDeleteDialog: (open: boolean) => void;
  onCloseTravelCancelDialog: () => void;
  onCloseBookingDetailsDialog: () => void;
  onBlockTime: (startTime: string, endTime: string) => Promise<void>;
  onModifyBlock: (startTime: string, endTime: string) => Promise<void>;
  onDeleteBlock: () => Promise<void>;
  onDeleteFromDialog: () => void;
  onCancelTravelBlock: () => Promise<void>;
  onAcceptBooking: (bookingId: number) => Promise<void>;
  onDeclineBooking: (bookingId: number, reason?: string) => Promise<void>;
  onCompleteBooking?: (bookingId: number, completionData: BookingCompletionData) => Promise<void>;
  actionInProgress?: number | null;
  progressValue?: number;
  showSuccessAnimation?: boolean;
}

export const DashboardDialogs: React.FC<DashboardDialogsProps> = ({
  serviceName,
  selectedDate,
  selectedTimeSlot,
  isBlockingDialogOpen,
  isModifyDialogOpen,
  isDeleteDialogOpen,
  isTravelCancelDialogOpen,
  isBookingDetailsOpen,
  selectedBlockedBooking,
  selectedTravelBlock,
  selectedBooking,
  loading = false,
  serviceId,
  onCloseBlockingDialog,
  onCloseModifyDialog,
  onCloseDeleteDialog,
  onCloseTravelCancelDialog,
  onCloseBookingDetailsDialog,
  onBlockTime,
  onModifyBlock,
  onDeleteBlock,
  onDeleteFromDialog,
  onCancelTravelBlock,
  onAcceptBooking,
  onDeclineBooking,
  onCompleteBooking,
  actionInProgress = null,
  progressValue = 0,
  showSuccessAnimation = false
}) => {
  // console.log("Dashboard dialogs props:", { selectedBooking, isBookingDetailsOpen, actionInProgress });
  
  return (
    <>
      {/* Block Time Dialog */}
      <BlockTimeDialog
        isOpen={isBlockingDialogOpen}
        onClose={onCloseBlockingDialog}
        selectedDate={selectedDate}
        selectedTimeSlot={selectedTimeSlot}
        onBlock={onBlockTime}
        loading={loading}
        serviceName={serviceName}
        serviceId={serviceId}
      />

      {/* Edit Block Dialog */}
      <EditBlockDialog
        isOpen={isModifyDialogOpen}
        onClose={onCloseModifyDialog}
        selectedStartTime={selectedBlockedBooking?.start_time ? parseStoredDateTime(selectedBlockedBooking.booking_date || selectedBlockedBooking.start_time, selectedBlockedBooking.start_time_local).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : ""}
        selectedEndTime={selectedBlockedBooking?.end_time ? parseStoredDateTime(selectedBlockedBooking.booking_date || selectedBlockedBooking.end_time, selectedBlockedBooking.end_time_local).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : ""}
        onUpdate={onModifyBlock}
        onDelete={onDeleteFromDialog}
        loading={loading}
        serviceName={serviceName}
        selectedDate={selectedDate}
        serviceId={serviceId}
        blockingId={selectedBlockedBooking?.id}
      />

      {/* Booking Deletion Dialog */}
      <BookingActionDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => onCloseDeleteDialog(false)}
        onConfirm={onDeleteBlock}
        loading={loading}
        title="Confirm Deletion"
        description="Are you sure you want to delete this blocked time?"
        confirmText="Delete Block"
        cancelText="Cancel"
        variant="destructive"
      />

      {/* Travel Block Cancel Dialog */}
      <TravelBlockCancelDialog
        isOpen={isTravelCancelDialogOpen}
        onClose={onCloseTravelCancelDialog}
        travelBlock={selectedTravelBlock}
        onConfirm={onCancelTravelBlock}
        loading={loading}
      />

      {/* Booking Details Dialog */}
      <UnifiedBookingDialog
        isOpen={isBookingDetailsOpen}
        onClose={onCloseBookingDetailsDialog}
        booking={selectedBooking}
        isAdminView={false}
        onAccept={onAcceptBooking}
        onDecline={onDeclineBooking}
        onComplete={onCompleteBooking}
        actionInProgress={actionInProgress}
        progressValue={progressValue}
        showSuccessAnimation={showSuccessAnimation}
      />
    </>
  );
};
